import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '../../redux/hooks';
import useFetchAppointments from '../../hooks/queries/useFetchAppointments';
import useFetchMedicationReminders from '../../hooks/queries/useFetchMedicationReminder';

import DashboardComponent from './dashboardComponent';

import { formatDates, generateDatesAroundToday } from '../../utils';

import {
  HOW_ARE_YOU_FEELING_PATH,
  ABOUT_YOUR_SURGERY_PATH,
  MEDICAL_CONSISTENCY_PATH,
} from '../../constants/paths';
import { APPOINTMENT_STATUS } from '../../constants';

import { TUserAuthInfo } from '../../redux/types';
import { TDosageFrequency } from '../../pages/medications/addMedication/types';
import { IMedicationReminder } from '../../constants/types';

export interface CreatedBy {
  id: number;
  profile_url: string;
  full_name: string;
}

export interface Medication {
  id: number;
  created_by: CreatedBy;
  patient_id: number;
  name: string;
  dosage: string;
  frequency: string;
  instruction: string;
  reminder: boolean;
  created_at: string;
  created_by_type: string;
  reminder_end_date: string;
  dosage_frequency: TDosageFrequency;
}

export interface MedicationEntry {
  id: number;
  date: string;
  dose: string;
  taken_on: string | null;
  patient_id: number;
}

export interface GroupedMedication {
  medication: Omit<Medication, 'id'>;
  entries: MedicationEntry[];
}

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    firstName,
    id: patientId,
    profile_url = '',
  } = (useAppSelector((state) => state?.auth?.currentUser) as TUserAuthInfo) ||
  {};

  const today = useMemo(() => new Date().toISOString(), []);

  const formattedDates = useMemo(
    () => formatDates(generateDatesAroundToday()),
    []
  );

  const handleLogout = () => {
    navigate('/profile');
  };

  const navigateToFeelingPath = () => {
    navigate(HOW_ARE_YOU_FEELING_PATH);
  };

  const navigateToMedicalConsistencyPath = () => {
    navigate(MEDICAL_CONSISTENCY_PATH);
  };

  const handleAppointmentDetailsClick = (appointmentId: number) => {
    navigate(`${ABOUT_YOUR_SURGERY_PATH}/${appointmentId}`);
  };

  const { appointments } = useFetchAppointments({
    page_no: 1,
    per_page: 10,
    from_date: today,
    patient_id: patientId,
    status: [APPOINTMENT_STATUS.CONFIRM],
    order_by: "date",
    order_direction: "asc"
  });

  const { medicationReminders } = useFetchMedicationReminders(patientId);

  const filterMedicationReminders = () => {
    const grouped: Record<number, GroupedMedication> = {};

    if (medicationReminders) {
      medicationReminders.forEach((medicationReminder: IMedicationReminder) => {
        const medId = medicationReminder.medication.id;
        if (!grouped[medId as number]) {
          grouped[medId] = {
            medication: {
              ...medicationReminder.medication,
              id: undefined,
            },
            entries: [],
          };
        }

        grouped[medId].entries.push({
          id: medicationReminder.id,
          date: medicationReminder.date,
          taken_on: medicationReminder.taken_on,
          patient_id: medicationReminder.patient_id,
          dose: medicationReminder.dose,
        });
      });
    }

    return Object.values(grouped);
  };

  return (
    <DashboardComponent
      onLogout={handleLogout}
      profileURL={profile_url || ''}
      fullName={firstName}
      formattedDates={formattedDates}
      onFeelingSelection={navigateToFeelingPath}
      onMedicalConsistencySelection={navigateToMedicalConsistencyPath}
      appointments={appointments}
      medicationReminders={filterMedicationReminders()}
      onAppointmentDetailsClick={handleAppointmentDetailsClick}
    />
  );
};

export default Dashboard;
